import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Popover, Transition } from '@headlessui/react';
import { BellAlertIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, FireIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { fetchAlerts, fetchUnreadAlertCount, markAlertRead, markAllAlertsRead } from '@/lib/admin/alerts';
import type { SubscriptionAlert, AlertSeverity } from '@/types/admin';
import { cn } from '@/lib/cn';

const SEVERITY_DOT: Record<AlertSeverity, string> = {
  INFO: 'bg-sky-500',
  WARNING: 'bg-amber-500',
  CRITICAL: 'bg-rose-500',
};

const SEVERITY_ICON: Record<AlertSeverity, typeof BellAlertIcon> = {
  INFO: InformationCircleIcon,
  WARNING: ExclamationTriangleIcon,
  CRITICAL: FireIcon,
};

function alertLink(a: SubscriptionAlert): string {
  if (a.user) return `/admin/users/${a.user.id}`;
  return '/admin/subscriptions';
}

export default function AdminAlertBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: count = 0 } = useQuery({
    queryKey: ['admin', 'alerts', 'unread-count'],
    queryFn: fetchUnreadAlertCount,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: latest } = useQuery({
    queryKey: ['admin', 'alerts', 'latest-unread'],
    queryFn: () => fetchAlerts({ unread: true, perPage: 10 }),
    refetchInterval: 60_000,
  });

  const items = latest?.items ?? [];

  const markOne = useMutation({
    mutationFn: (id: string) => markAlertRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'alerts'] });
    },
  });

  const markAll = useMutation({
    mutationFn: () => markAllAlertsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'alerts'] });
    },
  });

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <Popover.Button
            type="button"
            className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
            aria-label={t('admin.alerts.title')}
          >
            <BellAlertIcon className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                {count > 9 ? '9+' : count}
              </span>
            )}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 z-50 mt-2 w-96 max-w-[92vw] rounded-xl border border-gray-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">{t('admin.alerts.title')}</h3>
                <button
                  type="button"
                  className="text-xs font-medium text-indigo-600 hover:underline disabled:opacity-50"
                  disabled={count === 0 || markAll.isPending}
                  onClick={() => markAll.mutate()}
                >
                  {t('admin.alerts.markAllRead')}
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-gray-500">
                    {t('admin.alerts.noAlerts')}
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {items.map((a) => {
                      const Icon = SEVERITY_ICON[a.severity];
                      return (
                        <li
                          key={a.id}
                          className={cn(
                            'flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer',
                            !a.readAt && 'bg-indigo-50/30',
                          )}
                          onClick={() => {
                            if (!a.readAt) markOne.mutate(a.id);
                            navigate(alertLink(a));
                            close();
                          }}
                        >
                          <span className="mt-1 flex h-6 w-6 items-center justify-center">
                            <span className={cn('h-2 w-2 rounded-full', SEVERITY_DOT[a.severity])} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <Icon className="h-3.5 w-3.5 text-gray-500" />
                              <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                                {t(`admin.alerts.type.${a.type}`)}
                              </span>
                            </div>
                            <p className="mt-1 truncate text-sm text-gray-800">{a.message}</p>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {a.organization?.name ?? '—'} · {formatDistanceToNow(new Date(a.createdAt), { addSuffix: true })}
                            </p>
                          </div>
                          {!a.readAt && (
                            <button
                              type="button"
                              className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-emerald-600"
                              aria-label={t('admin.alerts.markRead')}
                              onClick={(e) => {
                                e.stopPropagation();
                                markOne.mutate(a.id);
                              }}
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs">
                <button
                  type="button"
                  className="font-medium text-indigo-600 hover:underline"
                  onClick={() => {
                    navigate('/admin/alerts');
                    close();
                  }}
                >
                  {t('admin.alerts.viewAll')}
                </button>
                <span className="text-gray-400">{count} {t('admin.alerts.unread')}</span>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
